import { Fragment, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { ProfessionalPaymentService } from '../../services/ProfessionalPaymentService';
import { ProfessionalPaymentRecordService } from '../../services/ProfessionalPaymentRecordService';
import { ProfessionalPaymentReport, ProfessionalConfig, PeriodType } from '../../types/professional-payment';
import {
  DollarSign,
  Users,
  Calendar,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  FileText,
  Download,
  CheckCircle,
  AlertCircle,
  Loader,
  Clock,
  History
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { parseLocalDate } from '../../utils/format';
import { PeriodSelector } from './PeriodSelector';
import { PaymentConfirmationModal } from './PaymentConfirmationModal';
import { PaymentHistoryTab } from './PaymentHistoryTab';
import { useAuth } from '../../context/AuthContext';

const ProfessionalPaymentPanel = () => {
  const { user, selectedClient } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [reports, setReports] = useState<ProfessionalPaymentReport[]>([]);
  const [professionals, setProfessionals] = useState<ProfessionalConfig[]>([]);
  const [expandedReport, setExpandedReport] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'reports' | 'history'>('reports');

  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());

  const [periodType, setPeriodType] = useState<PeriodType>('billing_period');
  const [startDate, setStartDate] = useState(format(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0), 'yyyy-MM-dd'));

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<ProfessionalPaymentReport | null>(null);
  const [savingPayment, setSavingPayment] = useState(false);

  const [pendingSummary, setPendingSummary] = useState({ totalPending: 0, totalAmount: 0, professionalCount: 0 });
  const [paidThisMonth, setPaidThisMonth] = useState({ totalPaid: 0, totalAmount: 0 });

  useEffect(() => {
    loadProfessionals();
    loadSummaries();
  }, [selectedClient?.clientId]);

  const loadSummaries = async () => {
    try {
      if (!selectedClient?.clientId) {
        setPendingSummary({ totalPending: 0, totalAmount: 0, professionalCount: 0 });
        setPaidThisMonth({ totalPaid: 0, totalAmount: 0 });
        return;
      }

      const pending = await ProfessionalPaymentRecordService.getPendingPaymentsSummary(selectedClient.clientId);
      setPendingSummary(pending);

      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const paid = await ProfessionalPaymentRecordService.getPaidPaymentsForPeriod(firstDay, lastDay, selectedClient.clientId);
      setPaidThisMonth(paid);
    } catch (error) {
      console.error('Error loading summaries:', error);
    }
  };

  const loadProfessionals = async () => {
    try {
      const configs = await ProfessionalPaymentService.getProfessionalConfigs(selectedClient?.clientId);
      setProfessionals(configs);
    } catch (error) {
      console.error('Error loading professionals:', error);
      toast.error('Erro ao carregar profissionais');
    }
  };

  const generateReports = async () => {
    setIsLoading(true);
    try {
      let customStart: Date | undefined;
      let customEnd: Date | undefined;

      if (periodType === 'date_range') {
        if (!startDate || !endDate) {
          toast.error('Por favor, selecione as datas de início e fim');
          setIsLoading(false);
          return;
        }
        customStart = parseLocalDate(startDate);
        customStart.setHours(0, 0, 0, 0);

        customEnd = parseLocalDate(endDate);
        customEnd.setHours(23, 59, 59, 999);

        if (customStart > customEnd) {
          toast.error('Data de início deve ser anterior à data de fim');
          setIsLoading(false);
          return;
        }
      }

      const generatedReports = await ProfessionalPaymentService.generateAllReports(
        selectedMonth,
        selectedYear,
        periodType,
        customStart,
        customEnd,
        selectedClient?.clientId
      );
      setReports(generatedReports);

      if (generatedReports.length === 0) {
        toast.info('Nenhum fisioterapeuta ativo com atendimentos no período');
      } else {
        toast.success('Relatório gerado com sucesso!');
      }
    } catch (error) {
      console.error('Error generating reports:', error);
      toast.error('Erro ao gerar relatório');
    } finally {
      setIsLoading(false);
    }
  };

  const exportReportToCSV = (report: ProfessionalPaymentReport) => {
    const customPeriod = periodType === 'date_range'
      ? `${format(parseLocalDate(startDate), 'dd/MM/yyyy')} - ${format(parseLocalDate(endDate), 'dd/MM/yyyy')}`
      : undefined;

    const csvContent = ProfessionalPaymentService.exportReportToCSV(
      report,
      periodType,
      customPeriod
    );

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `repasse_${report.professionalName}_${report.period.month}_${report.period.year}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Relatório exportado com sucesso!');
  };

  const handleMarkAsPaid = (report: ProfessionalPaymentReport) => {
    if (report.hasMissingPricingRule) {
      toast.error('Existem atendimentos sem regra cadastrada de repasse. Complete as regras antes de pagar.');
      return;
    }
    setSelectedReport(report);
    setPaymentModalOpen(true);
  };

  const handleConfirmPayment = async (paymentMethod: string, notes: string, paymentDate: Date) => {
    if (!selectedReport || !user || !selectedClient?.clientId) return;

    setSavingPayment(true);
    try {
      if (!selectedReport.professionalDoctorId) {
        throw new Error('Este profissional não possui vínculo válido em "doctors". Atualize o cadastro antes de confirmar o pagamento.');
      }

      const appointmentIds = selectedReport.appointments
        .map((appointment) => appointment.id)
        .filter((appointmentId): appointmentId is string => Boolean(appointmentId));

      const periodStartDate = periodType === 'date_range'
        ? parseLocalDate(startDate)
        : new Date(selectedReport.period.year, selectedReport.period.month - 1, 1);

      const periodEndDate = periodType === 'date_range'
        ? parseLocalDate(endDate)
        : new Date(selectedReport.period.year, selectedReport.period.month, 0);

      await ProfessionalPaymentRecordService.confirmPaymentWithAppointments({
        clientId: selectedClient.clientId,
        doctorId: selectedReport.professionalDoctorId,
        professionalName: selectedReport.professionalName,
        startDatePeriod: periodStartDate,
        endDatePeriod: periodEndDate,
        paymentTotalSessions: appointmentIds.length,
        paymentTotalAmount: selectedReport.payment.totalAmount,
        paymentSessionsWithEvolution: selectedReport.sessions.withEvolution,
        paymentMethod,
        paymentNotes: notes,
        paymentPaidBy: user.userId,
        paymentPaidAt: paymentDate,
        appointmentIds,
      });

      toast.success('Pagamento registrado com sucesso!');
      setPaymentModalOpen(false);
      setSelectedReport(null);
      await loadSummaries();
      await generateReports();
    } catch (error) {
      console.error('Error confirming payment:', error);
      const message = error instanceof Error ? error.message : 'Erro ao registrar pagamento';
      toast.error(message);
    } finally {
      setSavingPayment(false);
    }
  };

  const summary = ProfessionalPaymentService.calculateSummary(reports);

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const years = Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() - i);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Repasse de Profissionais</h2>
          <p className="text-sm text-gray-600 mt-1">
            Gerencie e visualize os repasses financeiros dos fisioterapeutas
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          <button
            onClick={() => setActiveTab('reports')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'reports'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Gerar Relatórios
            </span>
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'history'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Histórico de Pagamentos
            </span>
          </button>
        </nav>
      </div>

      {activeTab === 'history' ? (
        <PaymentHistoryTab />
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pagamentos Pendentes</p>
                  <p className="text-2xl font-bold text-yellow-600">{pendingSummary.totalPending}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    R$ {pendingSummary.totalAmount.toFixed(2)}
                  </p>
                </div>
                <Clock className="text-yellow-600" size={32} />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pagos Este Mês</p>
                  <p className="text-2xl font-bold text-green-600">{paidThisMonth.totalPaid}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    R$ {paidThisMonth.totalAmount.toFixed(2)}
                  </p>
                </div>
                <CheckCircle className="text-green-600" size={32} />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Profissionais com Pendências</p>
                  <p className="text-2xl font-bold text-blue-600">{pendingSummary.professionalCount}</p>
                </div>
                <Users className="text-blue-600" size={32} />
              </div>
            </div>
          </div>

      {/* Professional Configuration Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Users size={20} />
          Fisioterapeutas Cadastrados
        </h3>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Nome
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Calendário
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {professionals.map((prof) => (
                <tr key={prof.uid}>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {prof.displayName}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {prof.calendarName || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {prof.isActive ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle size={12} className="mr-1" />
                        Ativo
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Inativo
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 text-sm text-gray-600">
          <p>
            O status considera o vínculo financeiro ativo por sessão. Os valores são definidos em "Regras de Repasse".
          </p>
        </div>
      </div>

      {/* Period Selection */}
      <PeriodSelector
        periodType={periodType}
        onPeriodTypeChange={setPeriodType}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        month={selectedMonth}
        year={selectedYear}
        onMonthChange={setSelectedMonth}
        onYearChange={setSelectedYear}
      />

      {/* Generate Report Button */}
      <div className="flex justify-center">
        <button
          onClick={generateReports}
          disabled={isLoading}
          className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-lg font-medium shadow-md"
        >
          {isLoading ? (
            <>
              <Loader size={20} className="animate-spin" />
              Gerando Relatório...
            </>
          ) : (
            <>
              <FileText size={20} />
              Gerar Relatório
            </>
          )}
        </button>
      </div>

      {/* Summary Cards */}
      {reports.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Profissionais</p>
                <p className="text-2xl font-bold text-gray-900">{summary.totalProfessionals}</p>
              </div>
              <Users className="text-blue-600" size={32} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Profissionais Ativos</p>
                <p className="text-2xl font-bold text-gray-900">{summary.activeProfessionals}</p>
              </div>
              <CheckCircle className="text-green-600" size={32} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Sessões</p>
                <p className="text-2xl font-bold text-gray-900">{summary.totalSessions}</p>
              </div>
              <TrendingUp className="text-purple-600" size={32} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total a Repassar</p>
                <p className="text-2xl font-bold text-gray-900">
                  R$ {summary.totalAmount.toFixed(2)}
                </p>
              </div>
              <DollarSign className="text-green-600" size={32} />
            </div>
          </div>
        </div>
      )}

      {/* Reports Table */}
      {reports.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">
              Relatório Detalhado
              {periodType === 'date_range'
                ? ` - ${format(parseLocalDate(startDate), 'dd/MM/yyyy')} a ${format(parseLocalDate(endDate), 'dd/MM/yyyy')}`
                : ` - ${monthNames[selectedMonth - 1]} ${selectedYear}`}
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Profissional
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Calendário
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Sessões Concluídas
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Com Evolução
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Sem Evolução
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Total a Receber
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reports.map((report) => (
                  <Fragment key={report.professionalId}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {report.professionalName}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {report.calendarName}
                      </td>
                      <td className="px-6 py-4 text-sm text-center text-gray-900">
                        {report.sessions.completed}
                      </td>
                      <td className="px-6 py-4 text-sm text-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {report.sessions.withEvolution}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-center">
                        {report.sessions.withoutEvolution > 0 ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            {report.sessions.withoutEvolution}
                          </span>
                        ) : (
                          <span className="text-gray-400">0</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-right font-semibold text-gray-900">
                        R$ {report.payment.totalAmount.toFixed(2)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => exportReportToCSV(report)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Exportar CSV"
                          >
                            <Download size={18} />
                          </button>
                          <button
                            onClick={() => handleMarkAsPaid(report)}
                            disabled={!!report.hasMissingPricingRule}
                            className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            title={report.hasMissingPricingRule ? 'Há atendimentos sem regra de repasse cadastrada' : 'Marcar como Pago'}
                          >
                            Pagar
                          </button>
                          <button
                            onClick={() => setExpandedReport(
                              expandedReport === report.professionalId ? null : report.professionalId
                            )}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Ver Detalhes"
                          >
                            {expandedReport === report.professionalId ? (
                              <ChevronUp size={18} />
                            ) : (
                              <ChevronDown size={18} />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Details */}
                    {expandedReport === report.professionalId && (
                      <tr>
                        <td colSpan={7} className="px-6 py-4 bg-gray-50">
                          <div className="space-y-2">
                            <h4 className="text-sm font-semibold text-gray-900 mb-3">
                              Atendimentos Detalhados
                            </h4>
                            <div className="max-h-96 overflow-y-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                                      Data
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                                      Horário
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                                      Paciente
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                                      Local
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                                      Origem do Pagamento
                                    </th>
                                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">
                                      Evolução
                                    </th>
                                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">
                                      Fatura
                                    </th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">
                                      Valor
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {report.appointments.map((appointment) => (
                                    <tr key={appointment.id}>
                                      <td className="px-4 py-2 text-xs text-gray-900">
                                        {format(appointment.start, 'dd/MM/yyyy', { locale: ptBR })}
                                      </td>
                                      <td className="px-4 py-2 text-xs text-gray-600">
                                        {format(appointment.start, 'HH:mm', { locale: ptBR })}
                                      </td>
                                      <td className="px-4 py-2 text-xs text-gray-900">
                                        {appointment.patientName || appointment.subject || appointment.title || '-'}
                                      </td>
                                      <td className="px-4 py-2 text-xs text-gray-600">
                                        {appointment.location || '-'}
                                      </td>
                                      <td className="px-4 py-2 text-xs text-gray-600">
                                        {appointment.appointmentStatus?.paymentOrigin || '-'}
                                      </td>
                                      <td className="px-4 py-2 text-xs text-center">
                                        {appointment.appointmentStatus?.hasEvolution ? (
                                          <CheckCircle size={14} className="text-green-600 mx-auto" />
                                        ) : (
                                          <AlertCircle size={14} className="text-yellow-600 mx-auto" />
                                        )}
                                      </td>
                                      <td className="px-4 py-2 text-xs text-center">
                                        {appointment.appointmentStatus?.isBilled ? (
                                          <CheckCircle size={14} className="text-green-600 mx-auto" />
                                        ) : (
                                          <span className="text-gray-400">-</span>
                                        )}
                                      </td>
                                      <td className="px-4 py-2 text-xs text-right text-gray-900">
                                        R$ {(report.appointmentPricingById?.[appointment.id || ''] ?? 0).toFixed(2)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && reports.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <FileText size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Nenhum relatório gerado
          </h3>
          <p className="text-sm text-gray-600">
            Selecione um período e clique em "Gerar Relatório" para visualizar os repasses.
          </p>
        </div>
      )}

      {/* Payment Confirmation Modal */}
      {selectedReport && (
        <PaymentConfirmationModal
          isOpen={paymentModalOpen}
          onClose={() => {
            setPaymentModalOpen(false);
            setSelectedReport(null);
          }}
          report={selectedReport}
          onConfirm={handleConfirmPayment}
          loading={savingPayment}
        />
      )}
        </>
      )}
    </div>
  );
};

export default ProfessionalPaymentPanel;
